import { useEffect, useState } from 'react'
import {
  notificacoesApi,
  type MapaDeEspecialidades,
  type ModuloComMapa,
} from './notificacoesApi'
import { alternarNoMapa, siglaValida, siglasDoMapa } from './notificacoesRegras'

/**
 * O mapa especialidade -> pessoas de um BCF ou de uma triagem partilhada.
 *
 * O responsável de um conflito é uma especialidade (MAC, EST, HID, EEL,
 * ARQ...), não uma pessoa. Este mapa diz quem é cada especialidade
 * neste projecto: as notificações de "atribuído" vão para essas pessoas.
 * Só se podem escolher pessoas com acesso ao recurso.
 */
export default function EditorEspecialidades({
  modulo,
  contexto,
  aoFechar,
}: {
  modulo: ModuloComMapa
  contexto: string
  aoFechar?: () => void
}) {
  const [dados, setDados] = useState<MapaDeEspecialidades | null>(null)
  const [mapa, setMapa] = useState<Record<string, string[]>>({})
  const [nova, setNova] = useState('')
  const [extra, setExtra] = useState<string[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [estado, setEstado] = useState<'pronto' | 'a-gravar' | 'gravado'>('pronto')

  useEffect(() => {
    let vivo = true
    notificacoesApi
      .especialidades(modulo, contexto)
      .then((d) => {
        if (!vivo) return
        setDados(d)
        setMapa(d.mapa)
        setErro(null)
      })
      .catch((e: unknown) => {
        if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler as especialidades.')
      })
    return () => {
      vivo = false
    }
  }, [modulo, contexto])

  const gravar = async () => {
    setEstado('a-gravar')
    setErro(null)
    try {
      const d = await notificacoesApi.gravarEspecialidades(modulo, contexto, mapa)
      setDados(d)
      setMapa(d.mapa)
      setExtra([])
      setEstado('gravado')
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível gravar as especialidades.')
      setEstado('pronto')
    }
  }

  const acrescentar = () => {
    const s = nova.trim().toUpperCase()
    if (!siglaValida(s)) {
      setErro('A especialidade é uma sigla de 2 a 8 letras ou algarismos (por exemplo, GAS).')
      return
    }
    setErro(null)
    setExtra((xs) => (xs.includes(s) ? xs : [...xs, s]))
    setNova('')
  }

  const siglas = dados ? siglasDoMapa(mapa, [...dados.sugeridas, ...extra.filter((s) => !dados.sugeridas.includes(s))]) : []

  return (
    <section
      aria-label="Especialidades e pessoas"
      className="flex flex-col gap-2 rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-2 text-[11px]"
    >
      <div className="flex items-start gap-2">
        <p className="leading-snug text-[var(--color-text-muted)]">
          Quem responde por cada especialidade neste {modulo === 'bcf' ? 'BCF' : 'projecto de conflitos'}.
          Quando um {modulo === 'bcf' ? 'tópico' : 'conflito ou grupo'} passa a ter a especialidade
          como responsável, estas pessoas recebem a notificação.
        </p>
        {aoFechar && (
          <button
            type="button"
            onClick={aoFechar}
            className="ml-auto shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Fechar
          </button>
        )}
      </div>
      {erro && <p className="text-[var(--color-error-text)]">{erro}</p>}
      {!dados && !erro && <p className="text-[var(--color-text-muted)]">A carregar…</p>}
      {dados && dados.pessoas.length === 0 && (
        <p className="text-[var(--color-text-muted)]">Ainda não há pessoas com acesso a este recurso.</p>
      )}
      {dados && dados.pessoas.length > 0 && (
        <table className="w-full border-collapse">
          <caption className="sr-only">Pessoas por especialidade</caption>
          <tbody>
            {siglas.map((s) => (
              <tr key={s} className="border-b border-[var(--color-border)] last:border-b-0">
                <th scope="row" className="w-14 py-1 pr-2 text-left align-top font-semibold">
                  {s}
                </th>
                <td className="py-1">
                  <div className="flex flex-wrap gap-1">
                    {dados.pessoas.map((p) => {
                      const dentro = (mapa[s] ?? []).includes(p.id)
                      return (
                        <button
                          key={p.id}
                          type="button"
                          aria-pressed={dentro}
                          title={p.email}
                          onClick={() => {
                            setMapa((m) => alternarNoMapa(m, s, p.id))
                            setEstado('pronto')
                          }}
                          className={`rounded border px-1.5 py-0.5 ${
                            dentro
                              ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-bg)]'
                              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                          }`}
                        >
                          {p.nome || p.email}
                        </button>
                      )
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dados && (
        <div className="flex flex-wrap items-center gap-1">
          <label className="flex items-center gap-1 text-[var(--color-text-muted)]">
            Outra especialidade
            <input
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  acrescentar()
                }
              }}
              maxLength={8}
              placeholder="GAS"
              className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 uppercase"
            />
          </label>
          <button
            type="button"
            onClick={acrescentar}
            className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Acrescentar
          </button>
          <span className="ml-auto flex items-center gap-2">
            {estado === 'gravado' && (
              <span role="status" className="text-[var(--color-text-muted)]">
                Gravado.
              </span>
            )}
            <button
              type="button"
              onClick={() => void gravar()}
              disabled={estado === 'a-gravar' || dados.pessoas.length === 0}
              className="rounded bg-[var(--color-accent)] px-2 py-0.5 font-medium text-[var(--color-bg)] disabled:opacity-50"
            >
              {estado === 'a-gravar' ? 'A gravar…' : 'Gravar'}
            </button>
          </span>
        </div>
      )}
    </section>
  )
}
